import { Button } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    isBadRequest,
    Page,
    PermissionKeys,
    RowActions,
    Search,
    SortOrder,
    Table,
    useHasPermission,
    useQueryParams,
    WarningDialog,
} from '../../../shared';
import { DownloadCard } from '../../components';
import { useDeleteProductCategory, useProductCategoriesList } from '../../hooks';

export const ProductCategoriesPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.PRODUCT_CATEGORIES_WRITE);
    const [showCategoryInUse, setShowCategoryInUse] = useState(false);

    const { page, setPage, pageSize, setPageSize, sort, setSort, search, setSearch, debouncedSearch } =
        useQueryParams();

    const { isPending, data } = useProductCategoriesList({
        page: page,
        pageSize: pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
        search: debouncedSearch,
    });

    const { mutateAsync: deleteProductCategory } = useDeleteProductCategory();
    const onDelete = async (id: string) => {
        try {
            await deleteProductCategory(id);
        } catch (e) {
            if (isBadRequest(e)) return setShowCategoryInUse(true);
            throw e;
        }
    };

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('name'), minWidth: 150, flex: 1 },
        { field: 'abbreviation', headerName: t('abbreviation'), minWidth: 150, flex: 1 },
        { field: 'code', headerName: t('code'), minWidth: 150, flex: 1 },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 140,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id, code, name } }: GridCellParams) => (
                <>
                    <DownloadCard useIcon={true} code={code} name={name} />
                    {hasWritePermission && (
                        <RowActions
                            onEdit={() => navigate(`/admin/product-categories/${id}/edit`)}
                            onDelete={() => onDelete(id)}
                            deleteWarningTitle={t('productCategoryDeleteWarningTitle')}
                            deleteWarningText={t('productCategoryDeleteWarningText')}
                        />
                    )}
                </>
            ),
        },
    ];

    return (
        <Page
            title={t('productCategories')}
            actions={[
                <Search search={search} onSearch={setSearch} />,
                hasWritePermission && (
                    <Button
                        component={Link}
                        to="/admin/product-categories/new"
                        color="primary"
                        variant="contained"
                        data-testid="new-productCategory"
                    >
                        {t('newProductCategory')}
                    </Button>
                ),
            ]}
        >
            <Table
                rows={data?.data || []}
                columns={columns}
                rowCount={data?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
                onRowClick={(row) => hasWritePermission && navigate(`/admin/product-categories/${row.id}/edit`)}
            />
            <WarningDialog
                open={showCategoryInUse}
                onClose={() => setShowCategoryInUse(false)}
                title={t('categoryInUseTitle')}
                text={t('categoryInUseDescription')}
            />
        </Page>
    );
};
